import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { CalendarCheck, Phone } from 'lucide-react';
import { SITE } from '../data/site';
import Button from './ui/Button';

/**
 * Phone-only bottom bar (display rules in a11y.css: shown at <=768px): Call +
 * Book. Slides up after 300px of scroll; hidden while the chat is fullscreen
 * or the mobile menu is open, and not rendered on the booking chooser itself.
 * While it is up, <html> carries .ve-cta-bar-up so chrome.css can lift the
 * other floating buttons (chat, accessibility, Google rating) above it.
 * Styled by chrome.css .mobile-cta-bar.
 */
export default function MobileCTA() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const onChooser = pathname.replace(/\/?$/, '/') === SITE.booking.chooserPath;
  const up = visible && !chatOpen && !onChooser;

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleChatToggle = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setChatOpen(detail?.isOpen ?? false);
    };
    window.addEventListener('chatbot-toggle', handleChatToggle);
    return () => window.removeEventListener('chatbot-toggle', handleChatToggle);
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('ve-has-cta-bar');
    return () => root.classList.remove('ve-has-cta-bar');
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle('ve-cta-bar-up', up);
  }, [up]);

  if (chatOpen || onChooser) return null;

  return (
    <div className={`mobile-cta-bar${visible ? ' is-visible' : ''}`} role="navigation" aria-label="Quick actions">
      <Button
        variant="outline"
        href={`tel:${SITE.phone}`}
        aria-label={`Call Now, Van Every Chiropractic, ${SITE.phoneDisplay}`}
        iconBefore={<Phone aria-hidden="true" focusable="false" />}
      >
        Call Now
      </Button>
      <Button
        to={SITE.booking.chooserPath}
        aria-label="Book online"
        iconBefore={<CalendarCheck aria-hidden="true" focusable="false" />}
      >
        Book Online
      </Button>
    </div>
  );
}
